<script setup>
import {useBaseStore} from "~/stores/base";
import star10Percent from "@/assets/img/icon-star-10-percent.svg"
import star20Percent from "@/assets/img/icon-star-20-percent.svg"
import star30Percent from "@/assets/img/icon-star-30-percent.svg"
import star40Percent from "@/assets/img/icon-star-40-percent.svg"
import star50Percent from "@/assets/img/icon-star-50-percent.svg"
import star60Percent from "@/assets/img/icon-star-60-percent.svg"
import star70Percent from "@/assets/img/icon-star-70-percent.svg"
import star80Percent from "@/assets/img/icon-star-80-percent.svg"
import star90Percent from "@/assets/img/icon-star-90-percent.svg"
import star100Percent from "@/assets/img/icon-star-filled.svg"

const router = useRouter()
const route = useRoute()

/** props **/
const props = defineProps({
  fitInfo: {
    default: {},
    required: true
  }
})

const paddingLeft = ref(0)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const fullStars = computed(() => Math.floor(props.fitInfo.reviewAverage || 0))
const hasHalfStar = computed(() => props.fitInfo.reviewAverage % 1 >= 0.5)
const halfStarImg = computed(() => {
  const percent = Math.round(props.fitInfo.reviewAverage % 1 * 10) * 10
  switch (percent) {
    case 10:
      return star10Percent
    case 20:
      return star20Percent
    case 30:
      return star30Percent
    case 40:
      return star40Percent
    case 50:
      return star50Percent
    case 60:
      return star60Percent
    case 70:
      return star70Percent
    case 80:
      return star80Percent
    case 90:
      return star90Percent
    case 100:
      return star100Percent
  }
})
const emptyStars = computed(() => 5 - fullStars.value - (hasHalfStar.value ? 1 : 0))

/** methods **/
const calculatePaddingLeft = (score, trackLength, barLength) => {
  const maxPaddingLeft = trackLength - barLength;

  if (score < -1 || score > 1) {
    throw new Error("Score must be within the range of -1 to 1");
  }

  // Normalize score from [-1, 1] to [0, 1]
  const normalizedScore = (score + 1) / 2;

  // Calculate paddingLeft
  return normalizedScore * maxPaddingLeft;
}

/** lifecycle **/
onMounted(() => {
  const trackLength = document.getElementById('track').clientWidth
  const barLength = document.getElementById('bar').clientWidth
  paddingLeft.value = calculatePaddingLeft(props.fitInfo.scoreEvaluation, trackLength, barLength)
})

</script>

<template>
  <div class="module-product-rate-large-mobile" v-if="isMobile">
    <div class="rating">
      <div class="stars">
        <img src="@/assets/img/icon-star-filled.svg" alt="star" v-for="star in fullStars">
        <img :src="halfStarImg" alt="star" v-if="hasHalfStar">
        <img src="@/assets/img/icon-star-empty.svg" alt="star" v-for="star in emptyStars">
      </div>
      <div class="points">{{ fitInfo.reviewAverage }}</div>
      <div class="divider"> /</div>
      <div class="reviews">{{ fitInfo.reviewCount }} reviews</div>
    </div>
    <div class="fitting">
      <div class="bottom-title">
        FIT <span>(based on {{ fitInfo.totalEvaluation }} reviews)</span>
      </div>
      <div id="track" class="bottom-bar-container" :style="{paddingLeft: paddingLeft + 'px'}">
        <div id="bar" class="bar"/>
      </div>
      <div class="bottom-bottom">
        <div class="bottom-bottom-left">Runs Small</div>
        <div class="bottom-bottom-right">Runs Large</div>
      </div>
    </div>
  </div>
  <div class="module-product-rate-large-desktop" v-else>
    <div class="title">
      CUSTOMER RATING
    </div>
    <div class="rating">
      <div class="stars">
        <img src="@/assets/img/icon-star-filled.svg" alt="star" v-for="star in fullStars">
        <img :src="halfStarImg" alt="star" v-if="hasHalfStar">
        <img src="@/assets/img/icon-star-empty.svg" alt="star" v-for="star in emptyStars">
      </div>
      <div class="points">{{ fitInfo.reviewAverage }}</div>
      <div class="divider"> /</div>
      <div class="reviews">{{ fitInfo.reviewCount }} reviews</div>
    </div>
    <div class="fitting">
      <div class="bottom-title">
        FIT <span>(based on {{ fitInfo.totalEvaluation }} reviews)</span>
      </div>
      <div id="track" class="bottom-bar-container" :style="{paddingLeft: paddingLeft + 'px'}">
        <div id="bar" class="bar"/>
      </div>
      <div class="bottom-bottom">
        <div class="bottom-bottom-left">Runs Small</div>
        <div class="bottom-bottom-right">Runs Large</div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "src/assets/config";

.module-product-rate-large-mobile {
  padding: 0 16px;

  .rating {
    margin-top: 16px;
    padding: 10px 0;
    background-color: $color-BNTO-beige-light;
    display: flex;
    align-items: center;

    .stars {
      display: flex;

      img {
        width: 24px;
        height: 24px;
      }
    }

    .points {
      margin-top: 6px;
      margin-left: 8px;

      font-family: "TWK Lausanne";
      font-size: 12px;
      font-style: normal;
      font-weight: 650;
      line-height: 18px; /* 150% */
    }

    .divider {
      margin: 6px 8px 0 8px;
      /* Text xs/Regular */
      font-family: "TWK Lausanne";
      font-size: 12px;
      font-style: normal;
      font-weight: 300;
      line-height: 18px; /* 150% */
    }

    .reviews {
      margin-top: 6px;
      font-family: "TWK Lausanne";
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px; /* 150% */
    }
  }

  .fitting {
    margin-top: 20px;

    .bottom-title {
      /* Eyebrow/medium */
      font-family: "TWK Lausanne";
      font-weight: 650;
      font-size: 14px;
      font-style: normal;
      line-height: 18px; /* 128.571% */
      letter-spacing: -0.056px;
      text-transform: uppercase;

      span {
        font-family: "TWK Lausanne";
        font-size: 10px;
        font-style: normal;
        font-weight: 300;
        line-height: 12px;
        text-transform: lowercase;
      }
    }

    .bottom-bar-container {
      margin-top: 4px;
      width: 100%;
      background-color: $color-gray-200;
      height: 8px;
      border-radius: 4px;

      .bar {
        width: 80px;
        background-color: $color-gray-800;
        height: 8px;
        border-radius: 4px;
      }
    }

    .bottom-bottom {
      margin-top: 4px;

      /* Text sm/Regular */
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 300;
      line-height: 20px; /* 142.857% */

      display: flex;
      justify-content: space-between;

      .bottom-bottom-left {
      }

      .bottom-bottom-right {
      }
    }
  }
}

.module-product-rate-large-desktop {
  width: 600px;

  .title {
    /* Eyebrow/medium */
    font-family: "TWK Lausanne";
    font-weight: 650;
    font-size: 14px;
    font-style: normal;
    line-height: 18px; /* 128.571% */
    letter-spacing: -0.056px;
    text-transform: uppercase;
  }

  .rating {
    margin-top: 16px;
    padding: 16px;
    background-color: $color-BNTO-beige-light;
    display: flex;
    align-items: center;

    .stars {
      display: flex;

      img {
        width: 40px;
        height: 40px;
      }
    }

    .points {
      margin-top: 10px;
      margin-left: 16px;
      /* Headline/small */
      font-family: "Alternate Gothic No3 D";
      font-size: 36px;
      font-style: normal;
      font-weight: 400;
      line-height: 32px; /* 88.889% */
      text-transform: uppercase;
    }

    .divider {
      margin: 0 8px;
      /* Text xs/Regular */
      font-family: "TWK Lausanne";
      font-size: 12px;
      font-style: normal;
      font-weight: 300;
      line-height: 18px; /* 150% */
    }

    .reviews {
      font-family: "TWK Lausanne";
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px; /* 150% */
    }
  }

  .fitting {
    margin-top: 20px;

    .bottom-title {
      /* Eyebrow/medium */
      font-family: "TWK Lausanne";
      font-weight: 650;
      font-size: 14px;
      font-style: normal;
      line-height: 18px; /* 128.571% */
      letter-spacing: -0.056px;
      text-transform: uppercase;

      span {
        font-family: "TWK Lausanne";
        font-size: 10px;
        font-style: normal;
        font-weight: 300;
        line-height: 12px;
        text-transform: lowercase;
      }
    }

    .bottom-bar-container {
      margin-top: 4px;
      width: 100%;
      background-color: $color-gray-200;
      height: 8px;
      border-radius: 4px;

      .bar {
        width: 80px;
        background-color: $color-gray-800;
        height: 8px;
        border-radius: 4px;
      }
    }

    .bottom-bottom {
      margin-top: 4px;

      /* Text sm/Regular */
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 300;
      line-height: 20px; /* 142.857% */

      display: flex;
      justify-content: space-between;

      .bottom-bottom-left {
      }

      .bottom-bottom-right {
      }
    }
  }
}
</style>
